import React, { useState, useEffect } from 'react';
import { HiCheck, HiOutlinePhotograph } from 'react-icons/hi';
import { heroService } from '../../services/heroService';
import './styles/HeroManagement.css';

function HeroManagement({ selectedEdition }) {
  const [heroData, setHeroData] = useState({
    badge: { date: '', location: '' },
    title: '',
    subtitle: '',
    description: '',
    primaryButton: { text: '', action: 'participation' },
    secondaryButton: { text: '', action: 'learn-more', visible: false },
    backgroundImage: '',
    backgroundImagePath: '',
    scrollIndicator: { visible: true, text: '' }
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [isDatabaseEmpty, setIsDatabaseEmpty] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (selectedEdition) {
      loadHeroData();
    }
  }, [selectedEdition]);

  useEffect(() => {
    if (originalData) {
      const hasChanges = JSON.stringify(originalData) !== JSON.stringify(heroData);
      setHasUnsavedChanges(hasChanges);
    }
  }, [heroData, originalData]);

  const loadHeroData = async () => {
    if (!selectedEdition) return;
    
    try {
      setLoading(true);
      const data = await heroService.getHeroData(selectedEdition);
      
      if (!data) {
        // Database is empty, set default values
        setIsDatabaseEmpty(true);
        const defaultData = {
          badge: { date: '', location: '' },
          title: '',
          subtitle: '',
          description: '',
          primaryButton: { text: '', action: 'participation' },
          secondaryButton: { text: '', action: 'learn-more', visible: false },
          backgroundImage: '',
          backgroundImagePath: '',
          scrollIndicator: { visible: true, text: '' }
        };
        setHeroData(defaultData);
        setOriginalData(JSON.parse(JSON.stringify(defaultData)));
        setImagePreview('');
      } else {
        setHeroData(data);
        setOriginalData(JSON.parse(JSON.stringify(data)));
        setImagePreview(data.backgroundImage);
        setIsDatabaseEmpty(false);
      }
    } catch (error) {
      console.error('Error loading hero data:', error);
      setIsDatabaseEmpty(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await heroService.updateHeroData(heroData, selectedEdition);
      setOriginalData(JSON.parse(JSON.stringify(heroData)));
      setHasUnsavedChanges(false);
      setIsDatabaseEmpty(false);
      setShowSaveSuccess(true);
      
      setTimeout(() => {
        setShowSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving hero data:', error);
      alert('Erreur lors de la sauvegarde. Veuillez réessayer.');
    }
  };

  const handleReset = () => {
    if (originalData && window.confirm('Voulez-vous vraiment annuler toutes les modifications non sauvegardées ?')) {
      setHeroData(JSON.parse(JSON.stringify(originalData)));
      setImagePreview(originalData.backgroundImage);
      setHasUnsavedChanges(false);
    }
  };

  const updateHeroData = (updates) => {
    setHeroData(prev => ({ ...prev, ...updates }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner un fichier image valide.');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      alert('L\'image est trop volumineuse. Taille maximum: 10MB.');
      return;
    }

    try {
      setUploading(true);

      // Delete old image if exists
      if (heroData.backgroundImagePath) {
        await heroService.deleteHeroImage(heroData.backgroundImagePath);
      }

      // Upload new image
      const uploadResult = await heroService.uploadHeroImage(file, selectedEdition);
      
      // Update hero data with new image
      updateHeroData({ 
        backgroundImage: uploadResult.url,
        backgroundImagePath: uploadResult.path
      });
      setImagePreview(uploadResult.url);

    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Erreur lors du téléchargement de l\'image. Veuillez réessayer.');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateBadge = (field, value) => {
    updateHeroData({
      badge: {
        ...heroData.badge,
        [field]: value
      }
    });
  };

  const handleUpdatePrimaryButton = (field, value) => {
    updateHeroData({
      primaryButton: {
        ...heroData.primaryButton,
        [field]: value
      }
    });
  };

  const handleUpdateSecondaryButton = (field, value) => {
    updateHeroData({
      secondaryButton: {
        ...heroData.secondaryButton,
        [field]: value
      }
    });
  };

  const handleUpdateScrollIndicator = (field, value) => {
    updateHeroData({
      scrollIndicator: {
        ...heroData.scrollIndicator,
        [field]: value
      }
    });
  };

  if (!selectedEdition) {
    return <div className="admin-section">Please select an edition first</div>;
  }

  if (loading) {
    return <div className="admin-section">Loading...</div>;
  }

  return (
    <div className="admin-section">
      {/* Database Empty Warning */}
      {isDatabaseEmpty && (
        <div className="database-empty-warning">
          <div className="warning-content">
            <h3>Base de données vide pour l'édition {selectedEdition}</h3>
            <p>La base de données Hero est vide pour cette édition. Remplissez les champs ci-dessous et cliquez sur "Sauvegarder" pour initialiser les données.</p>
          </div>
        </div>
      )}

      {/* Save/Reset Actions */}
      {hasUnsavedChanges && (
        <div className="save-actions-bar">
          <div className="save-message">
            <span>Vous avez des modifications non sauvegardées</span>
          </div>
          <div className="save-buttons">
            <button onClick={handleReset} className="btn-secondary">
              Annuler les modifications
            </button>
            <button onClick={handleSave} className="btn-primary save-btn">
              <HiCheck /> Sauvegarder
            </button>
          </div>
        </div>
      )}

      {/* Success Message */}
      {showSaveSuccess && (
        <div className="save-success-message">
          <HiCheck /> Modifications sauvegardées avec succès !
        </div>
      )}

      {/* Badge Section */}
      <div className="section-header">
        <h3>Badge d'Information</h3>
      </div>
      <div className="form-group">
        <label>Date:</label>
        <input 
          type="text" 
          value={heroData.badge.date}
          onChange={(e) => handleUpdateBadge('date', e.target.value)}
          placeholder="Ex: November 20-21, 2025"
        />
      </div>
      <div className="form-group">
        <label>Lieu:</label>
        <input 
          type="text" 
          value={heroData.badge.location}
          onChange={(e) => handleUpdateBadge('location', e.target.value)}
          placeholder="Ex: Los Angeles, CA"
        />
      </div>

      {/* Title and Subtitle */}
      <div className="section-header">
        <h3>Titre et Sous-titre</h3>
      </div>
      <div className="form-group">
        <label>Titre principal:</label>
        <input 
          type="text" 
          value={heroData.title}
          onChange={(e) => updateHeroData({ title: e.target.value })}
          placeholder="Ex: CIGSIDA 2025"
        />
      </div>
      <div className="form-group">
        <label>Sous-titre:</label>
        <textarea 
          value={heroData.subtitle}
          onChange={(e) => updateHeroData({ subtitle: e.target.value })}
          placeholder="Entrez le sous-titre"
          rows="3"
        />
      </div>
      <div className="form-group">
        <label>Description (optionnelle):</label>
        <textarea 
          value={heroData.description}
          onChange={(e) => updateHeroData({ description: e.target.value })}
          placeholder="Entrez une description"
          rows="2"
        />
      </div>

      {/* Primary Button */}
      <div className="section-header">
        <h3>Bouton Principal</h3>
      </div>
      <div className="form-group">
        <label>Texte du bouton:</label>
        <input 
          type="text" 
          value={heroData.primaryButton.text}
          onChange={(e) => handleUpdatePrimaryButton('text', e.target.value)}
          placeholder="Ex: INSCRIPTION POUR ASSISTER"
        />
      </div>
      <div className="form-group">
        <label>Action (navigation):</label>
        <select 
          value={heroData.primaryButton.action}
          onChange={(e) => handleUpdatePrimaryButton('action', e.target.value)}
        >
          <option value="participation">Participation</option>
          <option value="about">À Propos</option>
          <option value="dates">Dates</option>
          <option value="objectives">Objectifs</option>
          <option value="contact">Contact</option>
        </select>
      </div>

      {/* Secondary Button */}
      <div className="section-header">
        <h3>Bouton Secondaire</h3>
      </div>
      <div className="form-group">
        <label>
          <input 
            type="checkbox" 
            checked={heroData.secondaryButton.visible}
            onChange={(e) => handleUpdateSecondaryButton('visible', e.target.checked)}
          />
          Afficher le bouton secondaire
        </label>
      </div>
      {heroData.secondaryButton.visible && (
        <>
          <div className="form-group">
            <label>Texte du bouton:</label>
            <input 
              type="text" 
              value={heroData.secondaryButton.text}
              onChange={(e) => handleUpdateSecondaryButton('text', e.target.value)}
              placeholder="Ex: Learn More"
            />
          </div>
          <div className="form-group">
            <label>Action (navigation):</label>
            <select 
              value={heroData.secondaryButton.action}
              onChange={(e) => handleUpdateSecondaryButton('action', e.target.value)}
            >
              <option value="learn-more">Learn More</option>
              <option value="about">À Propos</option>
              <option value="dates">Dates</option>
              <option value="objectives">Objectifs</option>
              <option value="contact">Contact</option>
            </select>
          </div>
        </>
      )}

      {/* Background Image */}
      <div className="section-header">
        <h3>Image d'Arrière-plan</h3>
      </div>
      <div className="form-group">
        <label>Image de fond:</label>
        <div className="image-upload-container">
          {imagePreview && (
            <div className="image-preview-container">
              <img 
                src={imagePreview} 
                alt="Background Preview" 
                className="image-preview"
                style={{ width: '100%', maxWidth: '400px', height: '200px', objectFit: 'cover' }}
              />
            </div>
          )}
          <div className="upload-controls">
            <input 
              type="file" 
              accept="image/*"
              onChange={handleImageUpload}
              id="background-upload"
              className="file-input"
              disabled={uploading}
            />
            <label htmlFor="background-upload" className={`upload-btn ${uploading ? 'uploading' : ''}`}>
              <HiOutlinePhotograph /> 
              {uploading ? 'Téléchargement...' : 'Choisir une image'}
            </label>
            <p className="upload-hint">
              JPG, PNG ou WebP (max. 10MB) - Recommandé: 1920x1080px
            </p>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="section-header">
        <h3>Indicateur de Défilement</h3>
      </div>
      <div className="form-group">
        <label>
          <input 
            type="checkbox" 
            checked={heroData.scrollIndicator.visible}
            onChange={(e) => handleUpdateScrollIndicator('visible', e.target.checked)}
          />
          Afficher l'indicateur de défilement
        </label>
      </div>
      {heroData.scrollIndicator.visible && (
        <div className="form-group">
          <label>Texte de l'indicateur:</label>
          <input 
            type="text" 
            value={heroData.scrollIndicator.text}
            onChange={(e) => handleUpdateScrollIndicator('text', e.target.value)}
            placeholder="Ex: SCROLL"
          />
        </div>
      )}
    </div>
  );
}

export default HeroManagement;
